import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaPlus, FaCrosshairs, FaFileImport } from "react-icons/fa";
import { Z_INDEX } from "@/utils/z-index";
import { ContextMenuItem } from "./context-menu-helpers";
import TrackerModal from "@/features/trackers/tracker-modal";

/**
 * NewTrackerButton component - button to create a new tracker from the sidebar.
 * Shows a dropdown menu with options to create a monitoring tracker or import.
 * Opens the TrackerModal when "Nouveau tracker" is clicked.
 */
const NewTrackerButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showTrackerModal, setShowTrackerModal] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    bottom: number;
    left: number;
    width: number;
  } | null>(null);

  // Handle button click - calculate position before opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        bottom: window.innerHeight - rect.top + 4,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsOpen(!isOpen);
  };

  const handleCreateTracker = () => {
    setShowTrackerModal(true);
    setIsOpen(false);
  };

  const handleCreateImport = () => {
    // TODO: Implement import functionality
    // For now, we can open the same modal - it has an import option
    setShowTrackerModal(true);
    setIsOpen(false);
  };

  const handleCloseModal = () => {
    setShowTrackerModal(false);
  };

  return (
    <>
      <div className="px-3 py-2">
        <motion.button
          ref={buttonRef}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleToggle}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-dashed transition-all ${
            isOpen
              ? "border-[var(--arc-accent)] text-[var(--arc-accent)]"
              : "border-[var(--arc-sidebar-border-accent)] text-[var(--arc-sidebar-text-secondary)] hover:text-[var(--arc-sidebar-text-primary)] hover:border-[var(--arc-accent)] hover:bg-[var(--arc-sidebar-accent-glow)]"
          }`}
        >
          <FaPlus size={12} />
          <span className="text-sm">Nouveau tracker</span>
        </motion.button>

        {/* Dropdown Menu - rendered via portal to escape sidebar overflow */}
        {isOpen &&
          menuPosition &&
          createPortal(
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0"
                style={{ zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU - 1 }}
                onClick={() => setIsOpen(false)}
              />

              {/* Menu */}
              <AnimatePresence>
                <motion.div
                  key="new-tracker-menu"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: "fixed",
                    bottom: menuPosition.bottom,
                    left: menuPosition.left,
                    width: menuPosition.width,
                    zIndex: Z_INDEX.SIDEBAR_CONTEXT_MENU,
                  }}
                  className="bg-[var(--arc-sidebar-background)] rounded-lg shadow-xl border border-[var(--arc-sidebar-border-accent)] py-1"
                >
                  <ContextMenuItem onClick={handleCreateTracker}>
                    <FaCrosshairs size={14} className="text-[var(--arc-accent)]" />
                    <div className="text-left">
                      <div className="font-medium">Nouveau tracker</div>
                      <div className="text-xs text-[var(--arc-sidebar-text-secondary)]">
                        Configurer un monitoring
                      </div>
                    </div>
                  </ContextMenuItem>

                  <ContextMenuItem onClick={handleCreateImport}>
                    <FaFileImport size={14} className="text-[var(--arc-accent)]" />
                    <div className="text-left">
                      <div className="font-medium">Importer des données</div>
                      <div className="text-xs text-[var(--arc-sidebar-text-secondary)]">
                        Depuis un fichier
                      </div>
                    </div>
                  </ContextMenuItem>
                </motion.div>
              </AnimatePresence>
            </>,
            document.body
          )}
      </div>

      {/* Tracker Creation Modal */}
      <TrackerModal open={showTrackerModal} onClose={handleCloseModal} />
    </>
  );
};

NewTrackerButton.displayName = "NewTrackerButton";

export default NewTrackerButton;
